export type BoardPiece = string | null
export type BoardState = BoardPiece[][]

export const FILES = ['a', 'b', 'c', 'd', 'e', 'f', 'g', 'h'] as const
export const RANKS = ['8', '7', '6', '5', '4', '3', '2', '1'] as const

const createEmptyBoard = (): BoardState =>
  Array.from({ length: 8 }, () => Array.from({ length: 8 }, () => null))

export const cloneBoard = (board: BoardState): BoardState =>
  board.map((row) => [...row])

export const squareToCoords = (
  square: string
): { row: number; col: number } | null => {
  if (square.length !== 2) return null

  const file = square[0]
  const rank = square[1]

  const col = FILES.indexOf(file as typeof FILES[number])
  const row = RANKS.indexOf(rank as typeof RANKS[number])

  if (col === -1 || row === -1) return null

  return { row, col }
}

export const coordsToSquare = (row: number, col: number): string => {
  const file = FILES[col] ?? 'a'
  const rank = RANKS[row] ?? '8'
  return `${file}${rank}`
}

export const parseFenPlacement = (fen: string): BoardState => {
  const board = createEmptyBoard()
  const ranks = fen.split('/')

  ranks.forEach((rank, rowIndex) => {
    if (rowIndex > 7) return

    let colIndex = 0

    for (const char of rank) {
      if (colIndex >= 8) break

      if (/[1-8]/.test(char)) {
        colIndex += Number(char)
        continue
      }

      if (/[prnbqkPRNBQK]/.test(char)) {
        board[rowIndex][colIndex] = char
        colIndex += 1
        continue
      }

      colIndex += 1
    }
  })

  return board
}

export const boardToFen = (board: BoardState): string =>
  board
    .map((row) => {
      let emptyCount = 0
      let rowFen = ''

      row.forEach((cell) => {
        if (!cell) {
          emptyCount += 1
          return
        }

        if (emptyCount) {
          rowFen += emptyCount
          emptyCount = 0
        }

        rowFen += cell
      })

      if (emptyCount) {
        rowFen += emptyCount
      }

      return rowFen || '8'
    })
    .join('/')

export interface ValidationResult {
  valid: boolean
  messages: string[]
}

export const validateBoard = (board: BoardState): ValidationResult => {
  let whiteKing = 0
  let blackKing = 0
  const messages: string[] = []
  let pawnOnEdge = false

  board.forEach((row, rowIndex) => {
    row.forEach((cell) => {
      if (!cell) return

      if (cell === 'K') whiteKing += 1
      if (cell === 'k') blackKing += 1

      if (rowIndex === 0 || rowIndex === 7) {
        if (cell.toLowerCase() === 'p') {
          pawnOnEdge = true
        }
      }
    })
  })

  if (whiteKing !== 1) {
    messages.push('Position must contain exactly one white king.')
  }

  if (blackKing !== 1) {
    messages.push('Position must contain exactly one black king.')
  }

  if (pawnOnEdge) {
    messages.push('Pawns cannot sit on rank 1 or 8.')
  }

  return {
    valid: messages.length === 0,
    messages,
  }
}
